import { AxiosResponse } from 'axios';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { Box, Button, Typography, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import TextField from 'components/text-field';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import Mui from 'materials';
import { postSiteForecast } from '../api/create-forecast-activity';
import { getForecastActivities } from '../api/get-forecast-activities';
import Paper from 'components/paper';

type FormValues = {
  comment: string;
};

type Props = {
  globalSiteId: string;
};

const ForecastActivities: React.FC<Props> = ({ globalSiteId }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const forecastDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const snackbarDispatch = useSnackbarDispatch();
  const theme = useTheme();

  const PAGE_NAME_ID = 'planningPrognosis.viewSite';

  // FORMS
  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      comment: '',
    },
    shouldUnregister: true,
  });

  // FETCH DATA
  const { data: forecastActivities, isLoading: forecastActivitiesIsLoading } = useGet(
    ['getForecastActivities', globalSiteId],
    (context) => getForecastActivities(String(context.queryKey[1])),
    {
      enabled: !!globalSiteId && account.company?.features.hasPlanningPrognosisForecast,
    }
  );

  // POST DATA
  const saveForecast = usePost<AxiosResponse, { id: string; comment: string }>(
    (request) => postSiteForecast(request.id, request.comment),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getForecastActivities']
      });
      forecastDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleCreateForecast = (request: any) => {
    saveForecast.mutate({ id: globalSiteId, comment: request.comment });
  };

  // RENDERS
  return account.company?.features.hasPlanningPrognosisForecast ? (
    <>
      <Paper transparent sx={{ marginTop: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id={`${PAGE_NAME_ID}.activitiesTitle`} />
          </Typography>

          <Dialog
            ref={forecastDialogRef}
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialogForecast.title` })}
            maxWidth="xs"
            button={
              <Button sx={{ mb: { xs: 3, md: 0 } }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.dialogForecast.dialogButton`} />
              </Button>
            }>
            <form onSubmit={handleSubmit(handleCreateForecast)}>
              {saveForecast.isPending && <LoadingSpinner overlay />}
              <TextField
                fullWidth
                sx={{ mt: 4 }}
                dark
                multiline
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialogForecast.commentLabel` })}
                controller={{
                  name: 'comment',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}
              />

              <Box display={'flex'} justifyContent={'end'}>
                <Button type="submit">
                  <FormattedMessage id="general.save" />
                </Button>
              </Box>
            </form>
          </Dialog>
        </Box>
        <List
          options={[
            { id: 'userId', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.forecastList.userIdLabel` }) },
            { id: 'date', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.forecastList.dateLabel` }) },
            { id: 'comment', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.forecastList.commentLabel` }) },
          ]}
          isLoading={forecastActivitiesIsLoading}
          noResult={{
            show: !!forecastActivities && forecastActivities?.length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.forecastList.noResults`} />,
          }}>
          {forecastActivities?.map((row, idx) => (
            <ListRow key={idx}>
              <ListCell align="left">{row.userId}</ListCell>
              <ListCell align="left">
                <FormattedDate
                  value={row.date}
                  day="2-digit"
                  month="short"
                  year="numeric"
                  hour="2-digit"
                  minute="2-digit"
                  hour12={false}
                />
              </ListCell>
              <ListCell align="left">{row.comment}</ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </>
  ) : null;
};

export default ForecastActivities;
